/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import React, { ReactNode, useEffect, useState } from 'react';
import { useLoading } from '@/context/LoadingContext';
import { FF_MOCK_AUTH } from '@shared/utils/feature-flag';

export const AUTH_GATE_TIMEOUT_MS = 15_000;

interface AuthGateProps {
  children: ReactNode;
}

/**
 * Blocks children from rendering until the initial authentication verification
 * is complete (signaled by the loading state becoming false). This ensures
 * routes never render before authentication state is known.
 *
 * The AuthenticationProvider (above this gate) calls addLoading() on mount and
 * removeLoading() when token validation finishes. This gate waits for that
 * loading cycle to complete before revealing children.
 *
 * In mock auth mode, the gate opens immediately.
 * 
 * Safety: A 15-second timeout prevents infinite blocking if isLoading
 * never becomes false (e.g., due to an unhandled exception in auth flow).
 */
const AuthGate: React.FC<AuthGateProps> = ({ children }) => {
  const { isLoading } = useLoading((state) => state);
  const [hasVerified, setHasVerified] = useState(() => FF_MOCK_AUTH);

  useEffect(() => {
    if (hasVerified) return;
    if (!isLoading) {
      setHasVerified(true);
    }
  }, [isLoading, hasVerified]);

  useEffect(() => {
    if (hasVerified) return;
    const timer = setTimeout(() => {
      console.error('[AuthGate] Safety timeout — forcing gate open after 15s');
      setHasVerified(true);
    }, AUTH_GATE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [hasVerified]);

  if (!hasVerified) return null;
  return <>{children}</>;
};

export default AuthGate;
